import { useState, type ReactNode, type JSX } from 'react'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'

/**
 * The filter row every list screen carries.
 *
 * One component because there were three, and they had already drifted: the same
 * controls sat in a Card on one screen and a bare Stack on another, and the
 * spacing between them was a different number on each.
 *
 * The part that matters is what it does on a phone. Nine stacked controls is
 * fifteen hundred pixels of filters before a single row of data — the Contacts
 * screen opened on a wall of empty dropdowns and somebody had to scroll past all
 * of them to find out whether the product had stored anybody. So below the
 * breakpoint everything but the first control folds away behind a button that
 * says how many filters are set, and the count is the point: a collapsed bar
 * that hides an active filter is how somebody spends ten minutes wondering where
 * their contacts went.
 *
 * It opens by default when a filter is already set, which is the case a shared
 * link creates: arriving at somebody else's view and being unable to see why it
 * is narrowed would be worse than the wall.
 */
export function FilterBar({
  primary,
  children,
  actions,
  activeCount,
}: Readonly<{
  /** The one control worth keeping visible on a phone — usually the search box. */
  primary: ReactNode
  /** The rest of the controls. */
  children: ReactNode
  /** Copy link, clear, and whatever acts on the selection. */
  actions?: ReactNode
  /** How many filters are set. Shown on the toggle so a hidden filter is never a surprise. */
  activeCount: number
}>): JSX.Element {
  const theme = useTheme()
  const narrow = useMediaQuery(theme.breakpoints.down('md'))
  const [open, setOpen] = useState(false)

  // A link that arrives already filtered opens showing why.
  const expanded = !narrow || open || activeCount > 0

  return (
    <Card>
      <CardContent sx={{ pb: 2, '&:last-child': { pb: 2 } }}>
        {/*
          ONE row on a wide screen, wrapping as it needs to.
          
          It was three stacked rows — the search box alone, then the filters,
          then the actions — which on a screen with two filters read as a mostly
          empty box three deep. The controls are one thing to a person setting
          them, so they sit on one line and wrap together.
        */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ rowGap: 1.5 }}
        >
          {primary}

          {narrow ? (
            <Button
              size="small"
              color="inherit"
              startIcon={
                <Badge badgeContent={activeCount} color="primary">
                  <TuneOutlinedIcon fontSize="small" />
                </Badge>
              }
              onClick={() => setOpen((was) => !was)}
              aria-expanded={expanded}
            >
              {expanded ? 'Fewer filters' : 'More filters'}
            </Button>
          ) : null}

          {/*
            On a wide screen the filters are simply part of the row above. Only
            the narrow layout needs them to fold, and only there do they get a
            container of their own — a Collapse on desktop would force its own
            line and put the empty row back.
          */}
          {narrow ? null : children}

          {actions ? (
            <>
              <Box sx={{ flexGrow: 1, minWidth: 0 }} />
              {actions}
            </>
          ) : null}
        </Stack>

        {narrow ? (
          <Collapse in={expanded} unmountOnExit={false}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ rowGap: 1.5, mt: 1.5 }}
            >
              {children}
            </Stack>
          </Collapse>
        ) : null}
      </CardContent>
    </Card>
  )
}
